import { scoreTone, signalLabel } from '../lib/format.js';

/**
 * Circular Bull Score gauge.
 *
 * Colour thresholds: green > 75, yellow 50-75, red < 50 (see `scoreTone`).
 *
 * @param {{score: number, signal?: string, caption?: string, detail?: string, size?: number}} props
 */
export default function ScoreBadge({ score, signal, caption = 'Bull Score', detail, size = 148 }) {
  const tone = scoreTone(score);
  const value = Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : 0;

  // Draw the arc as a fraction of the circle's circumference.
  const radius = 52;
  const stroke = 10;
  const circumference = 2 * Math.PI * radius;
  const dash = (value / 100) * circumference;

  return (
    <section className={`panel flex flex-col items-center gap-2 px-5 py-4 ${tone.border}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 128 128"
        role="img"
        aria-label={`${caption}: ${value} out of 100`}
      >
        <g transform="rotate(-90 64 64)">
          <circle cx="64" cy="64" r={radius} fill="none" stroke="#1d2534" strokeWidth={stroke} />
          <circle
            cx="64"
            cy="64"
            r={radius}
            fill="none"
            stroke={tone.stroke}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference - dash}`}
            style={{ transition: 'stroke-dasharray 700ms ease-out' }}
          />
        </g>
        <text
          x="64"
          y="63"
          textAnchor="middle"
          className={`fill-current font-mono text-[36px] font-semibold ${tone.text}`}
        >
          {value}
        </text>
        <text
          x="64"
          y="83"
          textAnchor="middle"
          className="fill-slate-500 text-[10px] uppercase tracking-[0.24em]"
        >
          / 100
        </text>
      </svg>

      <div className="flex flex-col items-center gap-1">
        <span className="panel-title">{caption}</span>
        <span className={`pill ${tone.chip}`} title={signalLabel(signal)}>
          {signal ?? tone.label}
        </span>
        {detail ? <p className="max-w-[15rem] text-center text-xs text-slate-500">{detail}</p> : null}
      </div>
    </section>
  );
}
